/**
 * Phase 1 §4.3 — Award rate context types and builder.
 *
 * The RateContext is the unit that feeds the (future, ctx-aware) calculator
 * in place of the hardcoded constants currently imported into schads.ts.
 *
 * Two builders are defined here:
 *
 * 1. `buildRateContextFromConstants(options?)` — synthesises a RateContext
 *    from the constants in regulatory-rates.ts. This is the OFF case for the
 *    `award_engine_versioned` feature flag. Callers that omit `asAt` keep the
 *    Jul-2025 wage-related allowance vintage. Passing `{ asAt, timezone }`
 *    selects dated hourly rows and populates wage-related allowance rows from
 *    `schadsAllowancesForShift` for that local date so a constants-backed
 *    context stays date-equivalent to the calculator's constants path.
 *
 * 2. `getRateContext()` — the future DB-backed lookup (queries
 *    award_rate_versions). Stubbed here; real implementation lives in
 *    apps/web/src/lib/payroll/rate-context.ts (post-Phase-1 follow-up,
 *    requires Supabase client).
 *
 * The schads.ts calculator does NOT yet consume RateContext. That refactor
 * is the next step on the Phase 1 critical path. Dual-run regression test
 * (byte-equal output across both paths) gates the calculator refactor.
 */
import {
  schadsRateScheduleFor,
  SCHADS_PERMANENT_PENALTY_RATES,
  SCHADS_CASUAL_PENALTY_RATES,
  SCHADS_OVERTIME_RATES,
  SCHADS_ALLOWANCES,
  SCHADS_BROKEN_SHIFT_FLAT,
} from './regulatory-rates';
import type {
  CompiledConditionalPenaltyLine,
  CompiledOvertimeRule,
  ManualBlockContract,
  StructuralBlock,
} from './payroll/eba-block-compiler';
import { DEFAULT_TZ, dateOnlyInTz } from './timezone';
import type { SchadsRatesOverride, WidenToNumber } from './schads';
import {
  schadsAllowancesForShift,
  schadsJul2025AllowanceVintage,
  type SchadsComputedWageRelatedAllowances,
} from './payroll/wage-related-allowances';

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export type ClassificationStream =
  | 'sacs'
  | 'home_care_aged'
  | 'home_care_disability'
  /** Nurses Award MA000034 cl 15.1 (non-aged-care). */
  | 'nurse'
  /** Nurses Award MA000034 cl 15.3 (aged-care branch — post-PR791118 work-value rates). */
  | 'aged_care_nurse'
  | 'hpss';
export type EmploymentTypeKey = 'PERMANENT' | 'CASUAL' | 'FIXED_TERM';
export type PenaltyBand =
  | 'WEEKDAY_DAY'
  | 'WEEKDAY_EVENING'
  /** Nurses Award MA000034 cl 20.2(a) — afternoon shift. */
  | 'WEEKDAY_AFTERNOON'
  | 'WEEKDAY_NIGHT'
  /** Nurses Award MA000034 cl 20.2(c) — permanent night shift worker. */
  | 'PERMANENT_NIGHT_SHIFT'
  | 'SATURDAY'
  | 'SUNDAY'
  | 'PUBLIC_HOLIDAY'
  | 'OVERTIME_FIRST_2H'
  | 'OVERTIME_AFTER_2H';
export type AllowanceBasis =
  | 'PER_SHIFT'
  | 'PER_KM'
  | 'PER_DAY'
  | 'PER_HOUR'
  | 'PER_WEEK_FT'
  | 'PER_WEEK_CAP'
  | 'PER_INCIDENT';

export interface RateLine {
  classificationStream: ClassificationStream;
  classificationLevel: string;
  payPoint: string | null;
  employmentType: EmploymentTypeKey | null;
  hourlyRate: number;
  ageBandMin?: number | null;
  ageBandMax?: number | null;
  /**
   * When true, `hourlyRate` already includes the 25% casual loading and must
   * not be multiplied again (Medea GS M7 / Marni Kaulima class).
   */
  includesCasualLoading?: boolean;
}

export interface PenaltyLine {
  band: PenaltyBand;
  employmentType: 'PERMANENT' | 'CASUAL';
  classificationStream: ClassificationStream | null;
  multiplier: number;
  clauseRef?: string;
  description?: string;
  /**
   * When true under BASE_PLUS_ENGINE_LOADING, `multiplier` is the permanent
   * base and the engine applies casual loading once. Under INCLUSIVE profiles
   * the multiplier is already the casual total.
   */
  includesCasualLoading?: boolean;
}

export interface AllowanceLine {
  allowanceCode: string;
  basis: AllowanceBasis;
  amount: number;
  weeklyCapAmount?: number | null;
  appliesToEmployment?: EmploymentTypeKey | null;
  appliesToStreams?: ClassificationStream[] | null;
  clauseRef?: string;
  description?: string;
}

export interface RateContext {
  versionId: string;
  awardCode: string;
  baseAwardCode?: string | null;
  effectiveFrom: string; // ISO yyyy-mm-dd
  effectiveTo: string | null;
  sourceUrl: string | null;
  isEba: boolean;
  ebaAgreementId?: string | null;
  /** Authoritative FWC identity of the resolved, tenant-scoped agreement. */
  ebaAgreementCode?: string | null;
  payRuleVersionId?: string | null;
  rateSource?: string | null;
  resolutionSource?: 'LEGACY' | 'TENANT_OVERRIDE' | 'STATE_BASE' | 'FEDERAL_BASE';
  tenantOverrideId?: string | null;
  /** Owner of the resolved legacy catalogue header, if provider-specific. */
  catalogueTenantId?: string | null;
  catalogueState?: string | null;
  state?: string | null;
  idempotencyKey?: string | null;
  /** Independent date-effective federal award floor, never the EA/worker override. */
  underlyingAwardVersions?: NonNullable<RateContext['underlyingAward']>[];
  underlyingAward?: {
    versionId: string;
    effectiveFrom: string;
    effectiveTo: string | null;
    rates: RateLine[];
  };
  rates: RateLine[];
  penalties: PenaltyLine[];
  allowances: AllowanceLine[];
  conditionalPenalties?: CompiledConditionalPenaltyLine[];
  requiredBands?: string[];
  requiredAllowanceCodes?: string[];
  requiredConditionalPenaltyCodes?: string[];
  manualContracts?: ManualBlockContract[];
  structuralUnsupported?: StructuralBlock[];
  /**
   * EA-supplied afternoon/night shift-character windows (finish-time based for
   * the Nurses EA cl 22.1) — data-driven so each agreement expresses its own
   * definitions. Absent → the engine uses its base-award defaults
   * (`NURSES_SHIFT_RULES`), so non-EA and unconfigured EA contexts are unchanged.
   */
  shiftCharacterRules?: import('./award-engines/shift-classifier').ShiftCharacterRules;
  /**
   * EA overtime tier metadata compiled from pay-rule blocks (GS cl 32(c)(i),
   * Nurses cl 22.22 loaded-rate overtime, etc.). Absent for non-EA contexts.
   */
  overtimeRules?: CompiledOvertimeRule[];
}

export const EBA_REQUIRED_PENALTY_COVERAGE_GAP_REASON = 'EBA_REQUIRED_PENALTY_BAND_MISSING';
export const EBA_REQUIRED_ALLOWANCE_COVERAGE_GAP_REASON = 'EBA_REQUIRED_ALLOWANCE_MISSING';
export const EBA_UNSUPPORTED_STRUCTURAL_BLOCK_COVERAGE_GAP_REASON =
  'EBA_UNSUPPORTED_STRUCTURAL_BLOCK';

/** Raised when multiple rate rows match the same employment specificity. */
export const RATE_ROW_AMBIGUOUS_EMPLOYMENT_COVERAGE_GAP_REASON = 'RATE_ROW_EMPLOYMENT_AMBIGUOUS';

export const COVERAGE_GAP_MULTIPLIER = Number.NaN;

export type EbaCoverageGap = {
  band: string;
  allowanceCode?: string;
  blockCode?: string;
  blockKind?: string;
  employment: string;
  reason: string;
};

type PenaltyMultiplierLookup = (band: string, employment: 'PERMANENT' | 'CASUAL') => number | null;
type AllowanceLineLookup = (
  code: string,
  employment: EmploymentTypeKey | null,
) => AllowanceLine | null | undefined;

function defaultPenaltyMultiplierLookup(
  ctx: RateContext,
  band: string,
  employment: 'PERMANENT' | 'CASUAL',
): number | null {
  return (
    ctx.penalties.find((p) => p.band === band && p.employmentType === employment)?.multiplier ??
    null
  );
}

function defaultAllowanceLineLookup(
  ctx: RateContext,
  code: string,
  employment: EmploymentTypeKey | null,
): AllowanceLine | null {
  if (employment === null) {
    return (
      ctx.allowances.find(
        (a) => a.allowanceCode === code && (a.appliesToEmployment ?? null) === null,
      ) ??
      ctx.allowances.find((a) => a.allowanceCode === code) ??
      null
    );
  }
  const employmentKey = employment === 'CASUAL' ? 'CASUAL' : 'PERMANENT';
  const specific = ctx.allowances.find(
    (a) => a.allowanceCode === code && a.appliesToEmployment === employmentKey,
  );
  if (specific) return specific;
  return (
    ctx.allowances.find(
      (a) => a.allowanceCode === code && (a.appliesToEmployment ?? null) === null,
    ) ?? null
  );
}

export function isRequiredPenaltyMissing(
  ctx: RateContext,
  band: string,
  employment: 'PERMANENT' | 'CASUAL',
  lookup?: PenaltyMultiplierLookup,
): boolean {
  if (!ctx.isEba || !(ctx.requiredBands ?? []).includes(band)) return false;
  const resolved = lookup
    ? lookup(band, employment)
    : defaultPenaltyMultiplierLookup(ctx, band, employment);
  return resolved === null || resolved === undefined;
}

export function isRequiredAllowanceMissing(
  ctx: RateContext,
  code: string,
  employment: EmploymentTypeKey | null,
  lookup?: AllowanceLineLookup,
): boolean {
  if (!ctx.isEba || !(ctx.requiredAllowanceCodes ?? []).includes(code)) return false;
  const resolved = lookup
    ? lookup(code, employment)
    : defaultAllowanceLineLookup(ctx, code, employment);
  return resolved === null || resolved === undefined;
}

export function pushPenaltyCoverageGap(
  coverageGaps: EbaCoverageGap[],
  band: string,
  employment: 'PERMANENT' | 'CASUAL',
  reason = EBA_REQUIRED_PENALTY_COVERAGE_GAP_REASON,
): void {
  if (
    coverageGaps.some(
      (gap) => gap.band === band && gap.employment === employment && gap.reason === reason,
    )
  ) {
    return;
  }
  coverageGaps.push({ band, employment, reason });
}

export function pushAllowanceCoverageGap(
  coverageGaps: EbaCoverageGap[],
  allowanceCode: string,
  employment: EmploymentTypeKey | null,
  reason = EBA_REQUIRED_ALLOWANCE_COVERAGE_GAP_REASON,
): void {
  const employmentLabel = employment ?? 'PERMANENT';
  if (
    coverageGaps.some(
      (gap) =>
        gap.allowanceCode === allowanceCode &&
        gap.employment === employmentLabel &&
        gap.reason === reason,
    )
  ) {
    return;
  }
  coverageGaps.push({
    band: allowanceCode,
    allowanceCode,
    employment: employmentLabel,
    reason,
  });
}

export function resolveRequiredPenalty(
  ctx: RateContext,
  band: string,
  employment: 'PERMANENT' | 'CASUAL',
  hardcodedFallback: number,
  options: {
    lookup?: PenaltyMultiplierLookup;
    coverageGaps?: EbaCoverageGap[];
    reason?: string;
  } = {},
): number {
  const resolved = options.lookup
    ? options.lookup(band, employment)
    : defaultPenaltyMultiplierLookup(ctx, band, employment);
  if (resolved !== null && resolved !== undefined) return resolved;

  if (isRequiredPenaltyMissing(ctx, band, employment, options.lookup)) {
    if (options.coverageGaps) {
      pushPenaltyCoverageGap(options.coverageGaps, band, employment, options.reason);
    }
    return COVERAGE_GAP_MULTIPLIER;
  }

  return hardcodedFallback;
}

export function resolveRequiredAllowance(
  ctx: RateContext,
  code: string,
  employment: EmploymentTypeKey | null,
  options: {
    lookup?: AllowanceLineLookup;
    coverageGaps?: EbaCoverageGap[];
    reason?: string;
  } = {},
): AllowanceLine | null {
  const resolved = options.lookup
    ? options.lookup(code, employment)
    : defaultAllowanceLineLookup(ctx, code, employment);
  if (resolved !== null && resolved !== undefined) return resolved;

  if (isRequiredAllowanceMissing(ctx, code, employment, options.lookup)) {
    if (options.coverageGaps) {
      pushAllowanceCoverageGap(options.coverageGaps, code, employment, options.reason);
    }
  }

  return null;
}

export function collectRequiredPenaltyCoverageGaps(
  ctx: RateContext,
  bands: readonly string[],
  employment: 'PERMANENT' | 'CASUAL',
  lookup?: PenaltyMultiplierLookup,
): EbaCoverageGap[] {
  const coverageGaps: EbaCoverageGap[] = [];
  for (const band of bands) {
    if (isRequiredPenaltyMissing(ctx, band, employment, lookup)) {
      pushPenaltyCoverageGap(coverageGaps, band, employment);
    }
  }
  return coverageGaps;
}

export function collectRequiredAllowanceCoverageGaps(
  ctx: RateContext,
  codes: readonly string[],
  employment: EmploymentTypeKey | null,
  lookup?: AllowanceLineLookup,
): EbaCoverageGap[] {
  const coverageGaps: EbaCoverageGap[] = [];
  for (const code of codes) {
    if (isRequiredAllowanceMissing(ctx, code, employment, lookup)) {
      pushAllowanceCoverageGap(coverageGaps, code, employment);
    }
  }
  return coverageGaps;
}

export function collectUnsupportedStructuralBlockCoverageGaps(ctx: RateContext): EbaCoverageGap[] {
  if (!ctx.isEba) return [];

  const coverageGaps: EbaCoverageGap[] = [];
  const seen = new Set<string>();
  for (const block of ctx.structuralUnsupported ?? []) {
    const blockCode = block.blockCode.trim();
    if (!blockCode) continue;
    const key = `${blockCode}|${block.blockKind}|${block.reason}`;
    if (seen.has(key)) continue;
    seen.add(key);
    coverageGaps.push({
      band: blockCode,
      blockCode,
      blockKind: block.blockKind,
      employment: 'ANY',
      reason: `${EBA_UNSUPPORTED_STRUCTURAL_BLOCK_COVERAGE_GAP_REASON}:${block.reason}`,
    });
  }
  return coverageGaps.sort((a, b) => {
    const byBlockCode = (a.blockCode ?? a.band).localeCompare(b.blockCode ?? b.band);
    if (byBlockCode !== 0) return byBlockCode;
    return a.reason.localeCompare(b.reason);
  });
}

export function coverageGapsField(coverageGaps: readonly EbaCoverageGap[] | undefined): {
  coverageGaps?: EbaCoverageGap[];
} {
  return coverageGaps && coverageGaps.length > 0 ? { coverageGaps: [...coverageGaps] } : {};
}

// ═══════════════════════════════════════════════════════════════════════════
// Constant-backed builder (the flag-OFF path)
// ═══════════════════════════════════════════════════════════════════════════

export type BuildRateContextFromConstantsOptions = {
  /**
   * Shift start used to select dated SCHADS hourly rates and allowance vintage.
   * Omitted → July-2026 hourly rates, with legacy July-2025 allowance flats.
   */
  asAt?: Date;
  /** IANA timezone for the local effective date of `asAt`. Defaults to Australia/Sydney. */
  timezone?: string;
};

function schadsWageRelatedAllowanceVintage(
  options?: BuildRateContextFromConstantsOptions,
): SchadsComputedWageRelatedAllowances {
  return options?.asAt
    ? schadsAllowancesForShift(options.asAt, options.timezone)
    : schadsJul2025AllowanceVintage();
}

function schadsAllowanceLinesFromVintage(
  wageRelated: SchadsComputedWageRelatedAllowances,
): AllowanceLine[] {
  // Wage-related rows and the separately dated vehicle expense follow the vintage.
  // Other expense/CPI flats retain their existing published values.
  return [
    {
      allowanceCode: 'SLEEPOVER',
      basis: 'PER_SHIFT',
      amount: wageRelated.allowances.sleepoverRate,
      clauseRef: 'cl 25.7(d)',
    },
    {
      allowanceCode: 'BROKEN_SHIFT_1',
      basis: 'PER_SHIFT',
      amount: wageRelated.brokenShiftFlat.oneBreak,
      clauseRef: 'cl 20.12(a)',
    },
    {
      allowanceCode: 'BROKEN_SHIFT_2_PLUS',
      basis: 'PER_SHIFT',
      amount: wageRelated.brokenShiftFlat.twoOrMoreBreaks,
      clauseRef: 'cl 20.12(b)',
    },
    {
      allowanceCode: 'KM',
      basis: 'PER_KM',
      amount: wageRelated.allowances.travelPerKm,
      clauseRef: wageRelated.allowances.travelPerKm === 1.05 ? 'cl 20.7(aa)' : 'cl 20.7(a)',
    },
    {
      allowanceCode: 'MEAL',
      basis: 'PER_INCIDENT',
      amount: SCHADS_ALLOWANCES.mealAllowance,
      clauseRef: 'cl 20.5',
    },
    {
      allowanceCode: 'FIRST_AID_FT_WEEKLY',
      basis: 'PER_WEEK_FT',
      amount: wageRelated.allowances.firstAidWeekly,
      weeklyCapAmount: wageRelated.allowances.firstAidWeekly,
      appliesToEmployment: 'PERMANENT',
      clauseRef: 'cl 20.6(a)',
    },
    {
      allowanceCode: 'FIRST_AID_PT_HOURLY',
      basis: 'PER_HOUR',
      amount: wageRelated.allowances.firstAidHourlyPartTime,
      weeklyCapAmount: wageRelated.allowances.firstAidWeekly,
      clauseRef: 'cl 20.6(b)',
    },
    {
      allowanceCode: 'UNIFORM_PER_SHIFT',
      basis: 'PER_SHIFT',
      amount: SCHADS_ALLOWANCES.uniformPerShift,
      weeklyCapAmount: SCHADS_ALLOWANCES.uniformWeekly,
      clauseRef: 'cl 20.2(b)',
    },
    {
      allowanceCode: 'UNIFORM_WEEKLY_CAP',
      basis: 'PER_WEEK_CAP',
      amount: SCHADS_ALLOWANCES.uniformWeekly,
      weeklyCapAmount: SCHADS_ALLOWANCES.uniformWeekly,
      clauseRef: 'cl 20.2(b)',
    },
    {
      allowanceCode: 'LAUNDRY_PER_SHIFT',
      basis: 'PER_SHIFT',
      amount: SCHADS_ALLOWANCES.laundryPerShift,
      weeklyCapAmount: SCHADS_ALLOWANCES.laundryWeeklyCap,
      clauseRef: 'cl 20.2(b)',
    },
    {
      allowanceCode: 'LAUNDRY_WEEKLY_CAP',
      basis: 'PER_WEEK_CAP',
      amount: SCHADS_ALLOWANCES.laundryWeeklyCap,
      weeklyCapAmount: SCHADS_ALLOWANCES.laundryWeeklyCap,
      clauseRef: 'cl 20.2(b)',
    },
    {
      allowanceCode: 'ON_CALL_WEEKDAY',
      basis: 'PER_DAY',
      amount: wageRelated.allowances.onCallWeekday,
      clauseRef: 'cl 20.11(a)',
    },
    {
      allowanceCode: 'ON_CALL_WEEKEND',
      basis: 'PER_DAY',
      amount: wageRelated.allowances.onCallWeekend,
      clauseRef: 'cl 20.11(b)',
    },
  ];
}

/**
 * Build a RateContext that mirrors the SCHADS constants in regulatory-rates.ts.
 * Used as the source of truth when feature flag `award_engine_versioned` is OFF
 * and as the verification fixture for the dual-run regression test.
 *
 * Base hourly rows and range select the same dated schedule as the calculator.
 * Without asAt, base hourly rows use the stable 2026-10-01 baseline.
 *
 * Pass `{ asAt, timezone }` to date-align hourly and wage-related allowance rows with
 * `schadsAllowancesForShift`. Omitting `asAt` preserves the Jul-2025 allowance vintage
 * for existing callers. `buildOverrideFromContext` remains a literal conversion
 * of the supplied context and must not be used to rewrite amounts from wall clock.
 */
export function buildRateContextFromConstants(
  options?: BuildRateContextFromConstantsOptions,
): RateContext {
  const schedule = schadsRateScheduleFor(
    options?.asAt ? dateOnlyInTz(options.asAt, options.timezone ?? DEFAULT_TZ) : undefined,
  );
  const rates: RateLine[] = [];

  // SACS L1.1 .. L8.3 — split classification + pay_point so the pay_point is
  // a separate column (mirrors DB shape). e.g. 'L3.2' -> level 'L3', pay_point '2'.
  for (const [key, hourlyRate] of Object.entries(schedule.levelRates)) {
    const dot = key.indexOf('.');
    const level = dot >= 0 ? key.slice(0, dot) : key;
    const payPoint = dot >= 0 ? key.slice(dot + 1) : null;
    rates.push({
      classificationStream: 'sacs',
      classificationLevel: level,
      payPoint,
      employmentType: null,
      hourlyRate,
    });
  }

  // Home Care Aged HCA1..HCA6 — flat per level.
  for (const [level, hourlyRate] of Object.entries(schedule.homeCareAgedRates)) {
    rates.push({
      classificationStream: 'home_care_aged',
      classificationLevel: level,
      payPoint: null,
      employmentType: null,
      hourlyRate,
    });
  }

  // Home Care Disability — keys like 'HCD1', 'HCD2.1', 'HCD2.2' etc.
  for (const [key, hourlyRate] of Object.entries(schedule.homeCareDisabilityRates)) {
    const dot = key.indexOf('.');
    const level = dot >= 0 ? key.slice(0, dot) : key;
    const payPoint = dot >= 0 ? key.slice(dot + 1) : null;
    rates.push({
      classificationStream: 'home_care_disability',
      classificationLevel: level,
      payPoint,
      employmentType: null,
      hourlyRate,
    });
  }

  // Penalty multipliers — permanent and casual variants.
  const penalties: PenaltyLine[] = [
    {
      band: 'SATURDAY',
      employmentType: 'PERMANENT',
      classificationStream: null,
      multiplier: SCHADS_PERMANENT_PENALTY_RATES.saturdayOrdinary,
      clauseRef: 'cl 26.1',
    },
    {
      band: 'SUNDAY',
      employmentType: 'PERMANENT',
      classificationStream: null,
      multiplier: SCHADS_PERMANENT_PENALTY_RATES.sundayOrdinary,
      clauseRef: 'cl 26.1',
    },
    {
      band: 'PUBLIC_HOLIDAY',
      employmentType: 'PERMANENT',
      classificationStream: null,
      multiplier: SCHADS_PERMANENT_PENALTY_RATES.publicHoliday,
      clauseRef: 'cl 26.3(a)',
    },
    {
      band: 'WEEKDAY_EVENING',
      employmentType: 'PERMANENT',
      classificationStream: null,
      multiplier: SCHADS_PERMANENT_PENALTY_RATES.afternoonShiftLoading,
      clauseRef: 'cl 29.1(a)',
    },
    {
      band: 'WEEKDAY_NIGHT',
      employmentType: 'PERMANENT',
      classificationStream: null,
      multiplier: SCHADS_PERMANENT_PENALTY_RATES.nightAfter12am,
      clauseRef: 'cl 29.1(b)',
    },
    {
      band: 'SATURDAY',
      employmentType: 'CASUAL',
      classificationStream: null,
      multiplier: SCHADS_CASUAL_PENALTY_RATES.saturdayOrdinary,
      clauseRef: 'cl 26.1',
    },
    {
      band: 'SUNDAY',
      employmentType: 'CASUAL',
      classificationStream: null,
      multiplier: SCHADS_CASUAL_PENALTY_RATES.sundayOrdinary,
      clauseRef: 'cl 26.1',
    },
    {
      band: 'PUBLIC_HOLIDAY',
      employmentType: 'CASUAL',
      classificationStream: null,
      multiplier: SCHADS_CASUAL_PENALTY_RATES.publicHoliday,
      clauseRef: 'cl 26.3(a)',
    },
    {
      band: 'WEEKDAY_EVENING',
      employmentType: 'CASUAL',
      classificationStream: null,
      multiplier: SCHADS_CASUAL_PENALTY_RATES.afternoonShiftLoading,
      clauseRef: 'cl 29.1(a)',
    },
    {
      band: 'WEEKDAY_NIGHT',
      employmentType: 'CASUAL',
      classificationStream: null,
      multiplier: SCHADS_CASUAL_PENALTY_RATES.nightAfter12am,
      clauseRef: 'cl 29.1(b)',
    },
  ];

  const allowances: AllowanceLine[] = schadsAllowanceLinesFromVintage(
    schadsWageRelatedAllowanceVintage(options),
  );

  return {
    versionId: '00000000-0000-0000-0000-000000000000',
    awardCode: 'MA000100',
    effectiveFrom: schedule.effectiveFrom,
    effectiveTo: schedule.effectiveTo,
    sourceUrl:
      'https://www.fairwork.gov.au/employment-conditions/awards/awards-summary/MA000100-summary',
    isEba: false,
    rates,
    penalties,
    allowances,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Override builder — converts a RateContext to a SchadsRatesOverride
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convert a `RateContext` (as returned by `buildRateContextFromConstants` or
 * the future DB-backed `getRateContext`) into a `SchadsRatesOverride` that
 * the SCHADS calculator can consume directly.
 *
 * This is the inverse of `buildRateContextFromConstants`: it JOINS the
 * (classificationLevel, payPoint) pair back into the keyed-record shape
 * the calculator expects (e.g. `'L3' + '2'` → `'L3.2'`).
 *
 * Penalty bands are mapped from the canonical band enum
 * (`SATURDAY` / `SUNDAY` / etc.) to the field names in
 * SCHADS_PERMANENT_PENALTY_RATES (`saturdayOrdinary`, etc.).
 *
 * Allowance codes are mapped to the corresponding field in SCHADS_ALLOWANCES
 * or SCHADS_BROKEN_SHIFT_FLAT.
 *
 * This function is a literal context conversion. It must not inspect wall
 * clock or rewrite a supplied context's allowance rows. Date alignment belongs
 * in `buildRateContextFromConstants({ asAt, timezone })` (or a DB-backed
 * context) before conversion.
 *
 * The dual-run regression test
 * (`packages/utils/src/__tests__/dual-run.test.ts`) asserts that for a wide
 * fixture set, `calculateShiftPay(input)` (constants path) produces the
 * same output as a ctx-derived override built from a date-equivalent
 * `buildRateContextFromConstants({ asAt: input.startTime, timezone })`.
 */
/** Identity of the resolved catalogue instrument, independent of line metadata
 * and MDM price override IDs. National date versions deliberately share a key. */
export function weeklyMinimumScopeKey(ctx: RateContext): string {
  const state = ctx.catalogueState !== undefined ? ctx.catalogueState : ctx.state;
  return JSON.stringify([
    ctx.baseAwardCode ?? ctx.awardCode,
    ctx.catalogueTenantId ?? null,
    state?.trim() || null,
    ctx.isEba,
    ctx.isEba ? (ctx.ebaAgreementId ?? ctx.versionId) : null,
  ]);
}

/** Weekly-floor lookup precedence: employment-specific, then stream-specific.
 * A tied best match is ambiguous evidence and must not be silently selected. */
export function findWeeklyMinimumAllowance(
  ctx: RateContext,
  employment: string,
  stream: string,
): AllowanceLine | null {
  const normalised = employment.trim().toUpperCase() === 'CASUAL' ? 'CASUAL' : 'PERMANENT';
  const ranked = ctx.allowances
    .filter(
      (line) =>
        line.allowanceCode === 'NAUSEOUS_WORK_MIN' &&
        (line.appliesToEmployment == null || line.appliesToEmployment === normalised) &&
        (line.appliesToStreams == null ||
          line.appliesToStreams.includes(stream as ClassificationStream)),
    )
    .map((line) => ({
      line,
      priority:
        (line.appliesToEmployment == null ? 0 : 2) + (line.appliesToStreams == null ? 0 : 1),
    }))
    .sort((a, b) => b.priority - a.priority);
  if (ranked.length > 1 && ranked[0]!.priority === ranked[1]!.priority) {
    throw new Error('weekly_minimum_catalogue_ambiguous');
  }
  return ranked[0]?.line ?? null;
}

export function buildOverrideFromContext(ctx: RateContext): SchadsRatesOverride {
  // Rates: split by classificationStream, JOIN level + payPoint.
  const levelRates: Record<string, number> = {};
  const homeCareAgedRates: Record<string, number> = {};
  const homeCareDisabilityRates: Record<string, number> = {};

  for (const r of ctx.rates) {
    const key = r.payPoint ? `${r.classificationLevel}.${r.payPoint}` : r.classificationLevel;
    if (r.classificationStream === 'sacs') levelRates[key] = r.hourlyRate;
    else if (r.classificationStream === 'home_care_aged') homeCareAgedRates[key] = r.hourlyRate;
    else if (r.classificationStream === 'home_care_disability')
      homeCareDisabilityRates[key] = r.hourlyRate;
  }

  // Penalty multipliers — start from constants, then overlay any matching bands
  // from ctx. Constants provide a baseline so partial ctx (missing some bands)
  // still produces a complete penalty set. WidenToNumber is imported from
  // schads.ts to keep the type definition single-sourced.
  const permanentPenaltyRates: WidenToNumber<typeof SCHADS_PERMANENT_PENALTY_RATES> = {
    ...SCHADS_PERMANENT_PENALTY_RATES,
  };
  const casualPenaltyRates: WidenToNumber<typeof SCHADS_CASUAL_PENALTY_RATES> = {
    ...SCHADS_CASUAL_PENALTY_RATES,
  };

  for (const p of ctx.penalties) {
    const target = p.employmentType === 'PERMANENT' ? permanentPenaltyRates : casualPenaltyRates;
    if (p.band === 'SATURDAY') target.saturdayOrdinary = p.multiplier;
    else if (p.band === 'SUNDAY') target.sundayOrdinary = p.multiplier;
    else if (p.band === 'PUBLIC_HOLIDAY') target.publicHoliday = p.multiplier;
    else if (p.band === 'WEEKDAY_EVENING') target.afternoonShiftLoading = p.multiplier;
    else if (p.band === 'WEEKDAY_NIGHT') target.nightAfter12am = p.multiplier;
    // Other bands (overtime tiers, weekday day) handled separately.
  }

  const applyMissingRequiredPenaltySentinels = (
    employment: 'PERMANENT' | 'CASUAL',
    target: WidenToNumber<typeof SCHADS_PERMANENT_PENALTY_RATES>,
  ) => {
    if (isRequiredPenaltyMissing(ctx, 'SATURDAY', employment)) {
      target.saturdayOrdinary = COVERAGE_GAP_MULTIPLIER;
    }
    if (isRequiredPenaltyMissing(ctx, 'SUNDAY', employment)) {
      target.sundayOrdinary = COVERAGE_GAP_MULTIPLIER;
    }
    if (isRequiredPenaltyMissing(ctx, 'PUBLIC_HOLIDAY', employment)) {
      target.publicHoliday = COVERAGE_GAP_MULTIPLIER;
    }
    if (isRequiredPenaltyMissing(ctx, 'WEEKDAY_EVENING', employment)) {
      target.afternoonShiftLoading = COVERAGE_GAP_MULTIPLIER;
    }
    if (isRequiredPenaltyMissing(ctx, 'WEEKDAY_NIGHT', employment)) {
      target.nightAfter12am = COVERAGE_GAP_MULTIPLIER;
    }
  };

  applyMissingRequiredPenaltySentinels('PERMANENT', permanentPenaltyRates);
  applyMissingRequiredPenaltySentinels('CASUAL', casualPenaltyRates);

  // Allowances — start from constants and overlay matched codes.
  const allowances: WidenToNumber<typeof SCHADS_ALLOWANCES> = { ...SCHADS_ALLOWANCES };
  const brokenShiftFlat: WidenToNumber<typeof SCHADS_BROKEN_SHIFT_FLAT> = {
    ...SCHADS_BROKEN_SHIFT_FLAT,
  };

  for (const a of ctx.allowances) {
    switch (a.allowanceCode) {
      case 'SLEEPOVER':
        allowances.sleepoverRate = a.amount;
        break;
      case 'BROKEN_SHIFT_1':
        brokenShiftFlat.oneBreak = a.amount;
        break;
      case 'BROKEN_SHIFT_2_PLUS':
        brokenShiftFlat.twoOrMoreBreaks = a.amount;
        break;
      case 'KM':
        allowances.travelPerKm = a.amount;
        break;
      case 'MEAL':
        allowances.mealAllowance = a.amount;
        break;
      case 'FIRST_AID_FT_WEEKLY':
        allowances.firstAidWeekly = a.amount;
        break;
      case 'FIRST_AID_PT_HOURLY':
        allowances.firstAidHourlyPartTime = a.amount;
        break;
      case 'UNIFORM_PER_SHIFT':
        allowances.uniformPerShift = a.amount;
        break;
      case 'UNIFORM_WEEKLY_CAP':
        allowances.uniformWeekly = a.amount;
        break;
      case 'LAUNDRY_PER_SHIFT':
        allowances.laundryPerShift = a.amount;
        break;
      case 'LAUNDRY_WEEKLY_CAP':
        allowances.laundryWeeklyCap = a.amount;
        break;
      case 'ON_CALL_WEEKDAY':
        allowances.onCallWeekday = a.amount;
        break;
      case 'ON_CALL_WEEKEND':
        allowances.onCallWeekend = a.amount;
        break;
      // Unknown codes (future EBA-introduced) ignored.
    }
  }

  // Overtime rates: not yet modelled as ctx penalty bands. Pass the constants
  // through so the calculator's overtime path still works. Future versions
  // will model overtime tiers in award_penalty_lines and convert here.
  const overtimeRates: WidenToNumber<typeof SCHADS_OVERTIME_RATES> = {
    ...SCHADS_OVERTIME_RATES,
  };

  return {
    ...((ctx.baseAwardCode ?? ctx.awardCode) === 'MA000018'
      ? { weeklyMinimumScopeKey: weeklyMinimumScopeKey(ctx) }
      : {}),
    levelRates,
    homeCareAgedRates,
    homeCareDisabilityRates,
    permanentPenaltyRates,
    casualPenaltyRates,
    overtimeRates,
    allowances,
    brokenShiftFlat,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// DB-backed lookup — stub
// ═══════════════════════════════════════════════════════════════════════════

export interface GetRateContextInput {
  tenantId: string;
  shiftDate: Date;
  awardCode: string;
  classificationStream: ClassificationStream;
  classificationLevel: string;
  payPoint?: string | null;
  employmentType: EmploymentTypeKey;
}

/**
 * Stub: real implementation queries award_rate_versions / award_rate_lines
 * / award_penalty_lines / award_allowance_lines from Supabase, filtered by
 * tenantId + awardCode + shiftDate. Lives in apps/web/src/lib/payroll/rate-context.ts
 * (a follow-up task; not on the Phase 1 cutover critical path).
 *
 * Until that is wired, `buildRateContextFromConstants()` is the canonical
 * source for the calculator (matching existing behaviour byte-for-byte).
 */
export async function getRateContext(_input: GetRateContextInput): Promise<RateContext> {
  throw new Error(
    'getRateContext() is not yet implemented. Use buildRateContextFromConstants() ' +
      'until the DB-backed lookup lands in apps/web/src/lib/payroll/rate-context.ts. ' +
      'Tracking: Phase 1 follow-up post-overnight cutover.',
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

export function rateRowCoverageGapBand(
  classificationStream: ClassificationStream,
  classificationLevel: string,
  payPoint: string | null,
): string {
  return `RATE:${classificationStream}:${classificationLevel}:${payPoint ?? 'null'}`;
}

export function pushRateRowCoverageGap(
  coverageGaps: EbaCoverageGap[],
  classificationStream: ClassificationStream,
  classificationLevel: string,
  payPoint: string | null,
  employment: 'PERMANENT' | 'CASUAL',
  reason: string = RATE_ROW_AMBIGUOUS_EMPLOYMENT_COVERAGE_GAP_REASON,
): void {
  const band = rateRowCoverageGapBand(classificationStream, classificationLevel, payPoint);
  if (
    coverageGaps.some(
      (gap) => gap.band === band && gap.employment === employment && gap.reason === reason,
    )
  ) {
    return;
  }
  coverageGaps.push({ band, employment, reason });
}

/**
 * Resolve a rate row by employment specificity: exact CASUAL/PERMANENT first,
 * then NULL fallback. Refuses ambiguous same-specificity rows.
 */
export function selectRateLineByEmployment(
  candidates: readonly RateLine[],
  employment: 'PERMANENT' | 'CASUAL',
): { rate: RateLine | null; ambiguous: boolean } {
  const exact = candidates.filter((row) => row.employmentType === employment);
  if (exact.length > 1) return { rate: null, ambiguous: true };
  if (exact.length === 1) return { rate: exact[0]!, ambiguous: false };

  const fallback = candidates.filter((row) => row.employmentType === null);
  if (fallback.length > 1) return { rate: null, ambiguous: true };
  if (fallback.length === 1) return { rate: fallback[0]!, ambiguous: false };

  return { rate: null, ambiguous: false };
}

export function findRate(
  ctx: RateContext,
  classificationStream: ClassificationStream,
  classificationLevel: string,
  payPoint: string | null = null,
  employment: 'PERMANENT' | 'CASUAL' | null = null,
  coverageGaps?: EbaCoverageGap[],
): RateLine | undefined {
  const candidates = ctx.rates.filter(
    (r) =>
      r.classificationStream === classificationStream &&
      r.classificationLevel === classificationLevel &&
      (r.payPoint ?? null) === (payPoint ?? null),
  );
  if (candidates.length === 0) return undefined;
  if (employment === null) return candidates[0];

  const { rate, ambiguous } = selectRateLineByEmployment(candidates, employment);
  if (ambiguous && coverageGaps) {
    pushRateRowCoverageGap(
      coverageGaps,
      classificationStream,
      classificationLevel,
      payPoint,
      employment,
    );
  }
  return rate ?? undefined;
}

export function findPenalty(
  ctx: RateContext,
  band: PenaltyBand,
  employmentType: 'PERMANENT' | 'CASUAL',
): PenaltyLine | undefined {
  return ctx.penalties.find((p) => p.band === band && p.employmentType === employmentType);
}

export function findAllowance(
  ctx: RateContext,
  code: string,
  employment: EmploymentTypeKey | null = null,
): AllowanceLine | undefined {
  return defaultAllowanceLineLookup(ctx, code, employment) ?? undefined;
}
